import * as React from 'react'
import { FieldValues } from 'react-hook-form'

export type Identificator = string | number

export enum LanguageEnum {
  CS = 'cs',
  DE = 'de',
  EN = 'en',
  RU = 'ru',
}

export enum CurrencyEnum {
  CZK = 'CZK',
  EUR = 'EUR',
}

export enum AccordionEnum {
  DESTINATION,
  GUESTS,
  HOTELS,
  TYPE_OF_STAY,
  ROOMS,
  CALENDAR,
}

export enum PaymentEnum {
  Bank = 'bank',
}

export enum Loaders {
  BOOKING = 'booking',
}

export interface ILang {
  cs_CZ: string | null
  de: string | null
  en: string | null
  ru: string | null
}

export type IImageVariants = Partial<Record<'w480' | 'w960' | 'w1440' | 'w1920', string>>

export interface IResponsiveImage {
  alt?: Partial<ILang>
  height?: number | null
  variants?: IImageVariants
  width?: number | null
}

export interface IHotelsParamsQuery {
  date_from?: string | null
  date_to?: string | null
  destination_id?: Identificator | null
  include_room_types?: boolean
  number_of_beds?: Identificator | null
  visit_type_id?: Identificator | null
}

export interface IHotelDetailParamsQuery {
  date_from?: string | null
  date_to?: string | null
  visit_type_id?: Identificator | null
}

export interface IVisitTypeCategoryParamsQuery {
  destination_id?: Identificator | null
  number_of_beds?: Identificator | null
}

export interface IInitDataParametersQuery {
  date_from?: string | null
  date_to?: string | null
  destination_name?: string | null
  guest?: number | null
  hotel_name?: string | null
  lang?: keyof ILang | 'cs' | null
  room_type_category?: string | null
  visit_type_name?: string | null
}

export interface IRouteQueryParams {
  affilUserName?: string | null
  destination?: string | null
  guest?: string | null
  hotel?: string | null
  lang?: keyof ILang | ILanguage | null
  roomType?: string | null
  visitEnd?: string | null
  visitStart?: string | null
  visitType?: string | null
}

export type IRouteQueryParamsWithDynamicParams = Record<string, string | null | undefined> &
  IRouteQueryParams

export interface IInitDataResponse {
  availability?: 'ON_DEMAND' | 'AVAILABLE' | 'NOT_AVAILABLE'
  date_from?: string | null
  date_to?: string | null
  destination?: IDestination | null
  hotel?: IHotel | null
  lowest_price?: IPrice | null
  price?: IPrice | null
  room_type?: IRoomType | null
  visit_type?: IVisitType | null
}

export interface IInitDataSuccessPayload extends Omit<IInitDataResponse, 'date_from' | 'date_to'> {
  date_from: Date | null
  date_to: Date | null
  destination: IDestination | null
  guest: number | null
}

export type IValidationState =
  | 'DESTINATIONS'
  | 'GUESTS'
  | 'VISIT_TYPE'
  | 'HOTEL'
  | 'ROOM_TYPE'
  | null

export interface IDestination {
  _id?: string
  id: Identificator
  name: ILang
}

export interface IChild {
  age: number | undefined
  id: number
}

export interface IPet {
  id: number
  species: string | undefined
}

export interface IVisitCategory {
  _id?: string
  id: Identificator
  name: ILang
  visitTypes: IVisitType[]
}

export type BoardingType =
  | 'ALL_INCLUSIVE'
  | 'BREAKFAST'
  | 'DINNER'
  | 'FULL_BOARD'
  | 'HALF_BOARD'
  | 'LUNCH'

export interface IVisitType extends IResponsiveImage {
  _id?: string
  boarding: BoardingType
  description: ILang
  hotels: IHotel[]
  id: Identificator
  image?: string | null
  lowest_price: IPrice
  loyalty_programs?: ILoyaltyProgram[]
  name: ILang
  nights_min: number
  notice: ILang
  services: ILang
  tags: ITag[]
  thumbnail?: string | null
}

export interface ITag {
  _id?: string
  id: Identificator
  name: ILang
  nights_min: number
}

export interface IRoomType extends IResponsiveImage {
  _id?: string
  beds: number
  booking_url: ILang
  category: ILang
  description: ILang
  facilities: string[]
  id: Identificator
  image?: string | null
  lowest_price: IPrice
  thumbnail?: string | null
}

export interface IPriceBreakdownDiscount {
  guest: number
  loyalty_program: ILoyaltyProgram
  price_discount: number
}

export interface IPromotion {
  // Discount amount for this promotion (whole room, in the response currency).
  amount: number,
  // Localized promotion name ({ cs_CZ, de, en, ru }).
  name: Record<string, string | null>,
  nights: number,
  percentage: number,
  // Discounted sub-range of the stay (UTC yyyy-MM-dd); valid_to is exclusive.
  valid_from: string
  valid_to: string
}

export interface IPriceBreakdown {
  base_price: number
  discounts: IPriceBreakdownDiscount[]
  // Discount promotions ("Slevy"). The top-level `amount` already has them
  // subtracted; this is the itemization (original = amount + Σ promotions[].amount).
  promotions?: IPromotion[]
}

export interface IPrice {
  cs_CZ: {
    amount?: number
    amount_per_night?: number
    currency: string,
    // Pre-discount per-night price; render struck-through when > amount_per_night.
    original_amount_per_night?: number,
    price_breakdown: IPriceBreakdown
  }
  de: {
    amount?: number
    amount_per_night?: number
    currency: string,
    // Pre-discount per-night price; render struck-through when > amount_per_night.
    original_amount_per_night?: number,
    price_breakdown: IPriceBreakdown
  }
  en: {
    amount?: number
    amount_per_night?: number
    currency: string,
    // Pre-discount per-night price; render struck-through when > amount_per_night.
    original_amount_per_night?: number,
    price_breakdown: IPriceBreakdown
  }
  is_final?: boolean
  ru: {
    amount?: number
    amount_per_night?: number
    currency: string,
    // Pre-discount per-night price; render struck-through when > amount_per_night.
    original_amount_per_night?: number,
    price_breakdown: IPriceBreakdown
  }
}

export interface IHotel extends IResponsiveImage {
  _id?: string,
  address?: {
    address_line: string
    city: string
    country_code: string
    postal_code: string
  },
  booking_url?: ILang,
  commercial_slogan?: ILang,
  description?: ILang,
  destination: {
    _id?: string
    id: Identificator
    name: ILang
  },
  email?: ILang,
  equipment?: ILang,
  id: Identificator
  image?: string | null
  lowest_price?: IPrice
  name: string
  phone_number?: string
  roomTypes?: IRoomType[]
  services?: ILang
  stars?: number
  thumbnail?: string | null
}

export interface ILoyaltyProgram {
  _id?: string
  id: Identificator
  name: ILang
}

export interface IStepper {
  step: number
  totalStep: number
}

export interface ITravelDate {
  from: Date | null
  to: Date | null
}

export interface INotification {
  closable: boolean
  isNetworkError: boolean
  message: React.ReactNode
  timestamp: number
}

export interface IApiFieldError {
  field: string
  message: string
  scope: 'services'
}

export interface IImages {
  loaded: number
  loading: number
}

export type ILanguage = 'en' | 'cs' | 'de' | 'ru'
export type ICurrency = 'EUR' | 'CZK'

export interface AppState {}

export type GenderValue = 'female' | 'male' | null

export enum CalendarModeEnum {
  FROM_DATE,
  TO_DATE,
}

export enum CalendarStateEnum {
  INITIAL,
  SET,
}

export interface ICalendar {
  checkout: IDateCheckout[]
  data: IDate[]
  date: Date
  isDidInvalid: boolean
  isFetching: boolean
  mode: CalendarModeEnum
  price: IPrice | null
  state: CalendarStateEnum
  travelDate: ITravelDate
}

interface IDialog {
  data: any | null
  isDidInvalid: boolean
  isFetching: boolean
}

export interface HotelDialog extends IDialog {
  data: IHotel | null
}

export interface RoomTypeDialog extends IDialog {
  data: IRoomType | null
}

export interface VisitTypeDialog extends IDialog {
  data: IVisitType | null
}

export interface PersonalFormData extends FieldValues {
  birth_date: string
  city: string
  country: string
  email: string
  first_name: string
  gender: GenderValue
  house_number: string
  last_name: string
  phone: string
  street: string
  zip_code: string
}

export interface LoyaltyProgramData extends FieldValues {
  code: string
  id: Identificator
}

export type ServiceFormData = {
  loyaltyPrograms: LoyaltyProgramData[]
  note: string | null
}

export interface GuestFormData extends FieldValues {
  birth_date: string | null
  first_name: string | null
  gender: GenderValue
  isEditing: boolean
  last_name: string | null
}

export interface GuestsFormData extends FieldValues {
  guests: GuestFormData[]
}

export interface SummaryFormData extends FieldValues {
  agree: boolean
  marketing: boolean
  payment: PaymentEnum | null
}

export interface IGuest {
  birth_date: string
  first_name: string
  gender: GenderValue
  last_name: string
  loyalty_program_code?: string
  loyalty_program_id?: Identificator
}

export interface IBookingResponse {
  _id?: string
  id: Identificator
}

export interface IBookingRequest {
  affilUserName?: string
  billed_to: {
    address?: {
      city: string
      country: string
      house_number: string
      street: string
      zip_code: string
    }
    consent_marketing: boolean
    email: string
    first_name: string
    gender: GenderValue
    last_name: string
    phone: string
  }
  binding_booking: boolean
  currency: ICurrency
  customer_note: string | null
  date_from: string
  date_to: string
  guests: IGuest[]
  hotel_id: number
  room_type_id: number
  visit_type_id: number
}

export interface IDatesParamsQuery {
  currency?: ICurrency | null
  dateMiddleCalendarDateCacheKey?: string
  dateToCalendarDateCacheKey?: string
  date_from?: string
  date_to?: string
  destinationId?: string | null,
  destination_id?: Identificator,
  hotelId?: string | null,
  hotel_id?: Identificator,
  initial?: boolean
  roomTypeId?: string | null,
  room_type_id?: Identificator,
  visitTypeId?: string | null,
  visit_type_id?: Identificator
}

export interface ICalendarDateMoney {
  amountMinor: number
  currency: ICurrency
  currencyExponent: number
}

export interface ICalendarDatePrice {
  amounts: Partial<Record<ICurrency, ICalendarDateMoney | null>>
  isFinal: boolean
}

export interface IDate {
  availability: 'NOT_AVAILABLE' | 'AVAILABLE' | 'ON_DEMAND'
  date: string
  price: ICalendarDatePrice | null
  selectable: boolean
}

export interface IDatesCheckoutParamsQuery {
  hotel_id?: Identificator
  room_type_id?: Identificator
  start_date: string
  visit_type_id?: Identificator
}

export interface IDateCheckout {
  date: string
}

export interface ILoaders {
  [Loaders.BOOKING]: boolean
}

export interface IUnknownObject {
  [key: string]: any
}

export interface IBackEndError {
  field: string
  message: string | string[]
}
